import type {
	TopologyReferenceIdentityDecision,
	TopologyReferenceIdentityPolicyInput,
} from '../../../../../types/reference/sketch-entity-reference/TopologyReferenceIdentityPolicy';

/**
 * topology 引用身份证据裁决策略。
 *
 * 设计说明：
 * 1. 来源族只负责限定候选范围，不作为物理身份票据；
 * 2. 仍能命中的独立物理证据或已确认物理范围必须共同指向同一候选；
 * 3. 局部地址只能在独立证据已经建立的范围内做最终消歧。
 */
export class TopologyReferenceIdentityPolicy {
	/**
	 * 裁决当前候选是否能够安全继承历史引用身份。
	 *
	 * @param input 候选、独立物理证据与可选局部地址证据。
	 * @returns 唯一确认、缺少证据或证据歧义。
	 */
	public resolve<TCandidate>(
		input: TopologyReferenceIdentityPolicyInput<TCandidate>,
	): TopologyReferenceIdentityDecision<TCandidate> {
		const independentEvidenceGroups = input.independentEvidenceGroups
			.filter((group) => group.length > 0);
		if (independentEvidenceGroups.length === 0) {
			return {status: 'missing'};
		}

		let candidatePool = [...input.candidates];
		for (const evidenceGroup of independentEvidenceGroups) {
			candidatePool = candidatePool.filter((candidate) => (
				evidenceGroup.includes(candidate)
			));
			if (candidatePool.length === 0) {
				return {status: 'ambiguous'};
			}
		}

		if (candidatePool.length === 1) {
			return {
				status: 'resolved',
				candidate: candidatePool[0]!,
			};
		}

		const localAddressCandidates = input.localAddressCandidates ?? [];
		const locallyResolvedCandidates = candidatePool.filter((candidate) => (
			localAddressCandidates.includes(candidate)
		));
		if (locallyResolvedCandidates.length === 1) {
			return {
				status: 'resolved',
				candidate: locallyResolvedCandidates[0]!,
			};
		}
		return {status: 'ambiguous'};
	}
}
